import { createApp, h } from "vue"
import { VueQueryPlugin } from "@tanstack/vue-query"
import { queryClient } from "@/services/query-client"
import App from "./App.vue"
import router from "./router"
import ElementPlus from "element-plus"
import i18n from "./i18n.js"
import store from "@/store"
import { useUserStore } from "@/store/user-store"
import trackClick from "@/services/analytics/directives/track-click"
import sentryService from "@/services/sentry-service"
import intercomService from "@/services/intercom-service"
import * as Sentry from "@sentry/vue"
import registerModule from "@/module-register"
import "./styles/index.css"
import "./styles/element/index.scss"
import "vue-sonner/style.css"

import { DateTime, Settings } from "luxon"
import { AppNameEnum } from "@vitable/asclepius-api-client-ts"
import { serverUrl } from "@/config"
import { version } from "../package.json"

import asclepiusConfig from "@common/config/asclepius-config"
import { isEmbeddedContext } from "@common/services/embed-service"
import registerLoginModule from "@/modules/login"
import registerPhoneClaimModule from "@/modules/phone-claim"
import registerEnrollmentNewModule from "@/modules/enrollment-new"
import registerHomeModule from "@/modules/home"
import registerInstructionsModule from "@/modules/instructions"
import registerSchedulingModule from "@/modules/scheduling"
import registerCareNavModule from "@/modules/care-nav"
import registerPlanShoppingModule from "@/modules/plan-shopping"

import embedService from "@common/services/embed-service"
import { initializeVitePreloadErrorHandler } from "@common/services/vite-preload-error-service"

// Setup shared configuration
asclepiusConfig.setAsclepiusServerUrl(serverUrl)
asclepiusConfig.setAppInfo({ appName: AppNameEnum.UserDashboard, appVersion: version })

const app = createApp({
  created() {
    // Skip user store init in iframe context — embed auth handles user setup
    // via ensureEmbedAuthenticated + reloadMe(). Running init() here would
    // read stale JWT tokens from localStorage and fire failing API calls.
    if (!isEmbeddedContext()) {
      useUserStore().init().then()
    }
  },
  render: () => h(App)
})

// * Add Directives
app.directive("track-click", trackClick)

// * Add custom properties
app.config.globalProperties.$luxon = DateTime
app.config.globalProperties.$luxonSettings = Settings

// Register modules
registerModule(registerLoginModule)
registerModule(registerPhoneClaimModule)
registerModule(registerEnrollmentNewModule)
registerModule(registerHomeModule)
registerModule(registerInstructionsModule)
registerModule(registerSchedulingModule)
registerModule(registerCareNavModule)
registerModule(registerPlanShoppingModule)

// MUST BE CALLED BEFORE app.mount
embedService.initializeEmbedContext(app)
sentryService.initializeSentry(app)
try {
  intercomService.initializeIntercom()
} catch (error) {
  // Intercom failing to load must not prevent the application from mounting.
  Sentry.captureException(error)
}
initializeVitePreloadErrorHandler({ router })

// * Add libraries
app.use(VueQueryPlugin, { queryClient }).use(i18n).use(router).use(store).use(ElementPlus)

app.mount("#app")
